import { useCallback, useEffect, useRef, useState } from "react";
import Modal from "react-modal";
import IOrder from "../../../models/IOrder";
import IRoute from "../../../models/IRoute";
import OwTitle from "../../shared/OwTitle/OwTitle";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwDateInput from "../../shared/OwDateInput/OwDateInput";
import { Moment } from "moment-timezone";
import OwButton from "../../shared/OwButton/OwButton";
import { useFormik } from "formik";
import IProcessDailyRouteDTO from "../../../models/DTO/IProcessDailyRouteDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IOnProcessDailyRouteDTO from "../../../models/DTO/IOnProcessDailyRouteDTO";
import INotification from "../../../models/INotification";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import { draggable, dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { setCustomNativeDragPreview } from "@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview";
import "./CreateDailyRouteFromOrdersSidebar.scss";
import { useAppContextNonNull } from "@contexts/AppContext";
import DailyRouteService from "../../../services/DailyRouteService";
import RouteService from "../../../services/RouteService";
import { DailyRouteStatusWarningModal } from "../../routes/DailyRouteStatusWarningModal/DailyRouteStatusWarningModal";
import { DailyRouteDoNotShipWarningModal } from "../../routes/DailyRouteDoNotShipWarningModal/DailyRouteDoNotShipWarningModal";
import OwCheckbox from "../../shared/OwCheckbox/OwCheckbox";
import { OwIconTooltip } from "../../shared/OwIconTooltip/OwIconTooltip";
import { toDeliveryDateKey } from "../../business/BusinessSettings/DeliveryDeadlines/deliveryDeadlineUtils";
import { buildDeliveryDateLegend, defaultRouteDate, formatLegendDate, IDeliveryDateLegend } from "./dailyRouteDeliveryDates";
import OwTranslate from "../../shared/OwTranslate/OwTranslate";

type Props = {
    orders: { guid: string; name: string; order: IOrder }[];
    cancelFn: (refresh?: boolean) => void;
};

interface IOrderRouteGroup {
    route: IRoute | null;
    orders: IOrder[];
}

// DraggableOrderItem Component
const DraggableOrderItem = ({
    order,
    isSelected,
    selectedOrderIds,
    isDragging,
    onToggleSelect,
    onDragStart,
    onDragEnd,
    legend,
    routeDate,
}: {
    order: IOrder;
    isSelected: boolean;
    selectedOrderIds: Set<number>;
    isDragging: boolean;
    onToggleSelect: (orderId: number, shiftKey: boolean) => void;
    onDragStart: (orderIds: number[]) => void;
    onDragEnd: () => void;
    legend: IDeliveryDateLegend;
    routeDate: string;
}) => {
    const ref = useRef<HTMLDivElement>(null);

    // Delivery date legend code for the row; flagged once a route date is chosen that it does not match.
    const deliveryDateKey = toDeliveryDateKey(order.delivery_date);
    const legendCodeForOrder = deliveryDateKey ? legend.codeByDate.get(deliveryDateKey) : undefined;
    const differsFromRouteDate = !!deliveryDateKey && !!routeDate && deliveryDateKey !== routeDate;

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        // A selected row drags the entire selection; an unselected row drags only itself
        const getDraggedOrderIds = () => (isSelected ? Array.from(selectedOrderIds) : [order.id]);

        return draggable({
            element: el,
            getInitialData: () => ({ orderIds: getDraggedOrderIds() }),
            onGenerateDragPreview: ({ nativeSetDragImage }) => {
                const count = getDraggedOrderIds().length;
                if (count <= 1) return;
                setCustomNativeDragPreview({
                    nativeSetDragImage,
                    render: ({ container }) => {
                        container.textContent = `${count} orders`;
                        container.className = "drag-preview-badge";
                    },
                });
            },
            onDragStart: () => onDragStart(getDraggedOrderIds()),
            onDrop: () => onDragEnd(),
        });
    }, [order.id, isSelected, selectedOrderIds, onDragStart, onDragEnd]);

    return (
        <div ref={ref} className={`draggable-order ${isDragging ? "dragging" : ""} ${isSelected ? "selected" : ""}`}>
            <OwCheckbox
                noLabel={true}
                checked={isSelected}
                className="me-2"
                fn={(e) => onToggleSelect(order.id, (e.nativeEvent as unknown as MouseEvent).shiftKey ?? false)}
            />
            <i className="fas fa-grip-vertical me-2 text-muted" />
            {legendCodeForOrder && (
                <span
                    className={`badge me-2 delivery-date-code ${differsFromRouteDate ? "bg-warning text-dark" : routeDate ? "bg-primary" : "bg-secondary"}`}
                    title={`${formatLegendDate(deliveryDateKey!)}${differsFromRouteDate ? " \u2260 " + formatLegendDate(routeDate) : ""}`}
                >
                    {legendCodeForOrder}
                </span>
            )}
            <span className="flex-grow-1 d-flex flex-column order-identity">
                <span className="fw-semibold">{order.Vendor?.name || order.name}</span>
                {order.Vendor?.name && <span className="text-muted small">{order.name}</span>}
            </span>
            <span className="text-muted small text-end">
                {order.Ship_To?.city} {order.Ship_To?.state} {order.Ship_To?.postal_code}
            </span>
        </div>
    );
};

// DroppableRouteSection Component
const DroppableRouteSection = ({
    group,
    groupIndex,
    onDrop,
    onRemove,
    selectedOrderIds,
    draggingIds,
    onToggleSelect,
    onDragStart,
    onDragEnd,
    onSetGroupSelection,
    legend,
    routeDate,
}: {
    group: IOrderRouteGroup;
    groupIndex: number;
    onDrop: (orderIds: number[], targetIndex: number) => void;
    onRemove: (groupIndex: number) => void;
    selectedOrderIds: Set<number>;
    draggingIds: Set<number>;
    onToggleSelect: (orderId: number, shiftKey: boolean) => void;
    onDragStart: (orderIds: number[]) => void;
    onDragEnd: () => void;
    onSetGroupSelection: (groupIndex: number, selected: boolean) => void;
    legend: IDeliveryDateLegend;
    routeDate: string;
}) => {
    const ref = useRef<HTMLDivElement>(null);
    const [isDraggedOver, setIsDraggedOver] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        return dropTargetForElements({
            element: el,
            getData: () => ({ targetGroupIndex: groupIndex }),
            onDragEnter: () => setIsDraggedOver(true),
            onDragLeave: () => setIsDraggedOver(false),
            onDrop: ({ source }) => {
                setIsDraggedOver(false);
                const { orderIds } = source.data as { orderIds: number[] };
                onDrop(orderIds, groupIndex);
            },
        });
    }, [groupIndex, onDrop]);

    const title = group.route?.name || "unassigned";
    const canRemove = group.route !== null && group.orders.length === 0;
    const selectedCount = group.orders.filter((o) => selectedOrderIds.has(o.id)).length;

    return (
        <div ref={ref} className={`route-section ${isDraggedOver ? "drag-over" : ""}`}>
            <div className="d-flex align-items-center justify-content-between mb-1">
                <OwLabel title={title} isBold={true} skipTranslate={!!group.route} />
                <div className="d-flex align-items-center gap-2">
                    {selectedCount > 0 && (
                        <span className="badge bg-primary">
                            {selectedCount}/{group.orders.length} <OwTranslate id={"selected"} defaultMessage={"Selected"} />
                        </span>
                    )}
                    {group.orders.length > 0 && selectedCount < group.orders.length && (
                        <button
                            type="button"
                            className="btn btn-sm btn-link p-0 small"
                            onClick={() => onSetGroupSelection(groupIndex, true)}
                        >
                            <OwTranslate id={"select_all"} defaultMessage={"Select All"} />
                        </button>
                    )}
                    {selectedCount > 0 && (
                        <button
                            type="button"
                            className="btn btn-sm btn-link p-0 small"
                            onClick={() => onSetGroupSelection(groupIndex, false)}
                        >
                            <OwTranslate id={"unselect_all"} defaultMessage={"Unselect All"} />
                        </button>
                    )}
                    {canRemove && (
                        <button type="button" className="btn btn-sm btn-link text-danger p-0" onClick={() => onRemove(groupIndex)}>
                            <i className="fas fa-times" />
                        </button>
                    )}
                </div>
            </div>
            {group.orders.map((order) => (
                <DraggableOrderItem
                    key={order.guid}
                    order={order}
                    isSelected={selectedOrderIds.has(order.id)}
                    selectedOrderIds={selectedOrderIds}
                    isDragging={draggingIds.has(order.id)}
                    onToggleSelect={onToggleSelect}
                    onDragStart={onDragStart}
                    onDragEnd={onDragEnd}
                    legend={legend}
                    routeDate={routeDate}
                />
            ))}
            {group.orders.length === 0 && group.route === null && <div className="text-muted small p-2 text-center">Drop orders here</div>}
            {group.orders.length === 0 && group.route !== null && (
                <div className="text-warning small p-2 text-center">
                    <i className="fas fa-exclamation-triangle me-1" />
                    No orders assigned - remove or add orders
                </div>
            )}
        </div>
    );
};

export const CreateDailyRouteFromOrdersSidebar = (props: Props) => {
    const { userModel, supplierModel, socket, uiContext } = useAppContextNonNull();

    const buildRouteGroups = (): { groups: IOrderRouteGroup[]; blockedCount: number } => {
        // Filter out blocked orders (preliminary, shipped, delivered, picked up)
        const allOrders = props.orders.map((od) => od.order);
        const { validOrders, blockedOrders } = DailyRouteService.filterBlockedOrders(allOrders);

        // Filter props.orders to only include valid orders
        const validOrderDetails = props.orders.filter((od) => validOrders.some((vo) => vo.id === od.order.id));

        const unassigned: IOrder[] = [];
        const routeMap = new Map<number, { route: IRoute; orders: IOrder[] }>();

        for (const orderDetail of validOrderDetails) {
            const order = orderDetail.order;
            if (!order.route_id || !order.Route) {
                unassigned.push(order);
            } else {
                const existing = routeMap.get(order.route_id);
                if (existing) {
                    existing.orders.push(order);
                } else {
                    routeMap.set(order.route_id, { route: order.Route, orders: [order] });
                }
            }
        }

        return {
            groups: [{ route: null, orders: unassigned }, ...Array.from(routeMap.values())],
            blockedCount: blockedOrders.length,
        };
    };

    const [routeGroupsData] = useState(() => buildRouteGroups());
    // Distinct delivery dates among the routable orders (lettered A, B, ...), driving the route date default
    // and the per-order codes so a mixed selection is visible at a glance.
    const [deliveryDateLegend] = useState(() => buildDeliveryDateLegend(routeGroupsData.groups.flatMap((group) => group.orders)));
    const [isDisabled, setIsDisabled] = useState(true);
    const [routeGroups, setRouteGroups] = useState<IOrderRouteGroup[]>(routeGroupsData.groups);
    const [availableRoutes, setAvailableRoutes] = useState<IRoute[]>([]);
    const [isLoadingRoutes, setIsLoadingRoutes] = useState(true);
    const [isActive, setIsActive] = useState(false);
    const [showStatusWarningModal, setShowStatusWarningModal] = useState(false);
    const [ordersNeedingStatusChange, setOrdersNeedingStatusChange] = useState<IOrder[]>([]);
    const [pendingFormValues, setPendingFormValues] = useState<{ route_date: string } | null>(null);
    const [showDoNotShipWarningModal, setShowDoNotShipWarningModal] = useState(false);
    const [doNotShipExcludedOrders, setDoNotShipExcludedOrders] = useState<IOrder[]>([]);
    const [pendingUpdatedGroups, setPendingUpdatedGroups] = useState<IOrderRouteGroup[] | null>(null);
    const [selectedOrderIds, setSelectedOrderIds] = useState<Set<number>>(new Set());
    const [draggingIds, setDraggingIds] = useState<Set<number>>(new Set());

    // Show notification once on mount if orders were blocked
    useEffect(() => {
        if (routeGroupsData.blockedCount > 0) {
            const notification: INotification = {
                message: `${routeGroupsData.blockedCount} order(s) excluded due to invalid status (preliminary, shipped, delivered, or picked up)`,
                position: NotificationPositionEnum.bc,
                level: NotificationLevelEnum.warning,
            };
            uiContext.notifyFn(null, notification);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Get routes that aren't already in use
    const unusedRoutes = availableRoutes.filter((route) => !routeGroups.some((g) => g.route?.id === route.id));

    const addRouteGroup = (route: IRoute) => {
        if (routeGroups.some((g) => g.route?.id === route.id)) return;
        setRouteGroups((prev) => [...prev, { route, orders: [] }]);
    };

    const removeRouteGroup = (groupIndex: number) => {
        const group = routeGroups[groupIndex];
        if (group.route === null || group.orders.length > 0) return;
        setRouteGroups((prev) => prev.filter((_, i) => i !== groupIndex));
    };

    const selectionAnchorRef = useRef<number | null>(null);

    const toggleOrderSelection = useCallback(
        (orderId: number, shiftKey: boolean) => {
            // Shift-click selects the whole range between the last clicked checkbox and this
            // one, in display order (across sections)
            const flatOrderIds = routeGroups.flatMap((g) => g.orders.map((o) => o.id));
            const anchorIndex = selectionAnchorRef.current !== null ? flatOrderIds.indexOf(selectionAnchorRef.current) : -1;
            const clickedIndex = flatOrderIds.indexOf(orderId);

            if (shiftKey && anchorIndex !== -1 && clickedIndex !== -1) {
                const [start, end] = anchorIndex < clickedIndex ? [anchorIndex, clickedIndex] : [clickedIndex, anchorIndex];
                const rangeIds = flatOrderIds.slice(start, end + 1);
                setSelectedOrderIds((prev) => {
                    const next = new Set(prev);
                    rangeIds.forEach((id) => next.add(id));
                    return next;
                });
            } else {
                setSelectedOrderIds((prev) => {
                    const next = new Set(prev);
                    if (next.has(orderId)) {
                        next.delete(orderId);
                    } else {
                        next.add(orderId);
                    }
                    return next;
                });
            }

            selectionAnchorRef.current = orderId;
        },
        [routeGroups]
    );

    const setGroupSelection = useCallback(
        (groupIndex: number, selected: boolean) => {
            const groupOrderIds = routeGroups[groupIndex].orders.map((o) => o.id);
            setSelectedOrderIds((prev) => {
                const next = new Set(prev);
                groupOrderIds.forEach((id) => (selected ? next.add(id) : next.delete(id)));
                return next;
            });
        },
        [routeGroups]
    );

    const onDragStart = useCallback((orderIds: number[]) => {
        setDraggingIds(new Set(orderIds));
    }, []);

    const onDragEnd = useCallback(() => {
        setDraggingIds(new Set());
    }, []);

    const moveOrders = useCallback((orderIds: number[], targetGroupIndex: number) => {
        const idSet = new Set(orderIds);
        setRouteGroups((prev) => {
            const newGroups = prev.map((g) => ({ ...g, orders: [...g.orders] }));
            const moved: IOrder[] = [];

            newGroups.forEach((group, i) => {
                if (i === targetGroupIndex) return;
                group.orders = group.orders.filter((o) => {
                    if (idSet.has(o.id)) {
                        moved.push(o);
                        return false;
                    }
                    return true;
                });
            });

            newGroups[targetGroupIndex].orders.push(...moved);
            return newGroups;
        });
        setSelectedOrderIds(new Set());
    }, []);

    const cancel = () => {
        props.cancelFn();
    };

    const getRoutes = async () => {
        try {
            const routes = await RouteService.getRoutes(userModel, supplierModel);
            setAvailableRoutes(routes);
        } catch (error) {
            console.error("Failed to fetch routes:", error);
        } finally {
            setIsLoadingRoutes(false);
        }
    };

    const processDailyRoute = (values: { route_date: string }) => {
        setIsActive(true);

        const dailyRoutes = routeGroups
            .filter((group) => group.route !== null && group.orders.length > 0)
            .map((group) => ({
                dailyRoute: {
                    route_date: values.route_date,
                    route_id: group.route!.id,
                },
                orders: group.orders.map((order) => order.id),
            }));

        if (dailyRoutes.length === 0) {
            const notification: INotification = {
                message: "no_orders_assigned_to_routes",
                position: NotificationPositionEnum.bc,
                level: NotificationLevelEnum.warning,
            };
            uiContext.notifyFn(null, notification);
            return;
        }

        const payload: IProcessDailyRouteDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            dailyRoutes,
        };

        socket.emit(EndpointsEnum.RoutesProcessDailyRoute, payload);
    };

    const onProcessDailyRoute = (_data: IOnProcessDailyRouteDTO) => {
        const notification: INotification = {
            message: "successfully_processed_new_daily_routes",
            position: NotificationPositionEnum.bc,
            level: NotificationLevelEnum.success,
        };

        uiContext.notifyFn(null, notification);

        props.cancelFn(true);

        setIsActive(false);
    };

    const onNotify = () => {
        setIsActive(false);
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.RoutesOnProcessDailyRoute, fn: onProcessDailyRoute },
        { endpoint: EndpointsEnum.Notify, fn: onNotify },
    ];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        getRoutes();

        return () => {
            for (const _socket of sockets) {
                socket.removeListener(_socket.endpoint, _socket.fn);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const continueSubmit = (values: { route_date: string }, groups: IOrderRouteGroup[]) => {
        // Check for empty route groups (route assigned but no orders)
        const emptyGroups = groups.filter((g) => g.route !== null && g.orders.length === 0);
        if (emptyGroups.length > 0) {
            const routeNames = emptyGroups.map((g) => g.route!.name).join(", ");
            const notification: INotification = {
                message: `The following route groups have no orders assigned: ${routeNames}. Please remove empty groups or assign orders to them.`,
                position: NotificationPositionEnum.bc,
                level: NotificationLevelEnum.warning,
                skipMessageTranslate: true,
            };
            uiContext.notifyFn(null, notification);
            return;
        }

        // Get all orders that will be submitted
        const ordersToSubmit = groups.filter((group) => group.route !== null && group.orders.length > 0).flatMap((group) => group.orders);

        // Check for orders needing status change (not in READY_FOR_DELIVERY or READY_FOR_PICKUP)
        const needsStatusChange = DailyRouteService.getOrdersNeedingStatusChange(ordersToSubmit);

        if (needsStatusChange.length > 0) {
            setOrdersNeedingStatusChange(needsStatusChange);
            setPendingFormValues(values);
            setShowStatusWarningModal(true);
        } else {
            processDailyRoute(values);
        }
    };

    const handleSubmit = (values: { route_date: string }) => {
        // Check for orders whose "Do Not Ship Before" date is after the route date
        const allExcluded: IOrder[] = [];
        const updatedGroups = routeGroups.map((group) => {
            if (group.route === null) return group;
            const { validOrders, excludedOrders } = DailyRouteService.filterDoNotShipBeforeOrders(group.orders, values.route_date);
            allExcluded.push(...excludedOrders);
            return { ...group, orders: validOrders };
        });

        if (allExcluded.length > 0) {
            // Show confirmation modal instead of hard-removing
            setDoNotShipExcludedOrders(allExcluded);
            setPendingUpdatedGroups(updatedGroups);
            setPendingFormValues(values);
            setShowDoNotShipWarningModal(true);
            return;
        }

        continueSubmit(values, routeGroups);
    };

    const confirmProceed = () => {
        setShowStatusWarningModal(false);
        if (pendingFormValues) {
            processDailyRoute(pendingFormValues);
        }
        setPendingFormValues(null);
        setOrdersNeedingStatusChange([]);
    };

    const cancelStatusWarning = () => {
        setShowStatusWarningModal(false);
        setPendingFormValues(null);
        setOrdersNeedingStatusChange([]);
    };

    const confirmDoNotShipRemoval = () => {
        setShowDoNotShipWarningModal(false);
        if (pendingUpdatedGroups && pendingFormValues) {
            setRouteGroups(pendingUpdatedGroups);
            continueSubmit(pendingFormValues, pendingUpdatedGroups);
        }
        setPendingUpdatedGroups(null);
        setDoNotShipExcludedOrders([]);
    };

    const cancelDoNotShipWarning = () => {
        setShowDoNotShipWarningModal(false);
        setPendingFormValues(null);
        setPendingUpdatedGroups(null);
        setDoNotShipExcludedOrders([]);
    };

    const formik = useFormik({
        initialValues: {
            // The orders' shared delivery date when they agree, today when none has one, blank when mixed.
            route_date: defaultRouteDate(deliveryDateLegend),
        },
        onSubmit: (values) => {
            handleSubmit(values);
        },
        validate: (values) => {
            const errors: any = {};

            if (!values.route_date) {
                errors.route_date = "Required";
            }

            return errors;
        },
    });

    useEffect(() => {
        const hasValidDate = !!formik.values.route_date;
        const hasUnassignedOrders = routeGroups[0]?.orders.length > 0;
        const hasAssignedOrders = routeGroups.some((g) => g.route !== null && g.orders.length > 0);
        setIsDisabled(!hasValidDate || hasUnassignedOrders || !hasAssignedOrders);
    }, [formik.values.route_date, routeGroups]);

    return (
        <div className={"CreateDailyRouteFromOrdersSidebar p-2"}>
            <div className={"row"}>
                <div className={"col-12"}>
                    <div className="text-end">
                        <button className="btn btn-danger btn-sm" onClick={cancel}>
                            <i className="far fa-times" />
                        </button>
                    </div>
                    <OwTitle className={"mb-2"} leftIconName={"fad fa-truck-front"} title={"create_daily_route"} />
                    <hr />
                </div>
            </div>

            <div className="row">
                <form onSubmit={formik.handleSubmit}>
                    <div className="mb-3">
                        <div>
                            <OwLabel title={"route_date"} isBold={true} />
                        </div>
                        <div>
                            <OwDateInput
                                selectedDate={formik.values.route_date}
                                fn={(date: Moment) => {
                                    const e = {
                                        target: {
                                            value: date.format("YYYY-MM-DD"),
                                            name: "route_date",
                                        },
                                    };

                                    formik.handleChange(e);
                                }}
                            />
                        </div>
                        {deliveryDateLegend.entries.length > 0 && (
                            <div className="delivery-date-legend small">
                                {deliveryDateLegend.entries.length > 1 && !formik.values.route_date && (
                                    <div className="text-warning mb-1">
                                        <i className="fas fa-exclamation-triangle me-1" />
                                        <OwTranslate
                                            id={"route_date_mixed_delivery_dates"}
                                            defaultMessage={"The orders have different delivery dates. Pick one to use as the route date."}
                                        />
                                    </div>
                                )}
                                <div className="d-flex flex-wrap gap-2">
                                    {deliveryDateLegend.entries.map((entry) => {
                                        const isRouteDate = entry.date === formik.values.route_date;

                                        return (
                                            <button
                                                key={entry.date}
                                                type="button"
                                                className={`btn btn-sm delivery-date-legend-entry ${isRouteDate ? "btn-primary" : "btn-outline-secondary"}`}
                                                onClick={() => formik.setFieldValue("route_date", entry.date)}
                                            >
                                                <span className="badge bg-light text-dark me-1">{entry.code}</span>
                                                {formatLegendDate(entry.date)}
                                                <span className="ms-1 opacity-75">({entry.orderCount})</span>
                                            </button>
                                        );
                                    })}
                                    {deliveryDateLegend.undatedCount > 0 && (
                                        <span className="text-muted align-self-center">
                                            <OwTranslate id={"no_delivery_date"} defaultMessage={"No delivery date"} /> (
                                            {deliveryDateLegend.undatedCount})
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Add Route Group Dropdown */}
                    {!isLoadingRoutes && unusedRoutes.length > 0 && (
                        <div className="mb-3">
                            <OwLabel title={"add_route_group"} isBold={true} />
                            <select
                                className="form-select form-select-sm"
                                onChange={(e) => {
                                    const routeId = parseInt(e.target.value);
                                    const route = availableRoutes.find((r) => r.id === routeId);
                                    if (route) addRouteGroup(route);
                                    e.target.value = "";
                                }}
                                defaultValue=""
                            >
                                <option value="" disabled>
                                    Select route...
                                </option>
                                {unusedRoutes.map((route) => (
                                    <option key={route.id} value={route.id}>
                                        {route.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Route Sections with Drag and Drop */}
                    <div className="d-flex align-items-center justify-content-end gap-1 mb-1">
                        <span className="text-muted small">
                            <OwTranslate id={"selection_tips"} defaultMessage={"Selection Tips"} />
                        </span>
                        <OwIconTooltip
                            iconClassName="fas fa-info-circle text-muted pointer"
                            placement="left"
                            tooltipContent={
                                <ul className="mb-0 ps-3 text-start">
                                    <li>
                                        <OwTranslate
                                            id={"tooltip_route_orders_bulk_drag"}
                                            defaultMessage={"Check orders, then drag any checked row to move them all together."}
                                        />
                                    </li>
                                    <li>
                                        <OwTranslate
                                            id={"tooltip_route_orders_shift_click"}
                                            defaultMessage={"Shift-click a checkbox to select a range of orders."}
                                        />
                                    </li>
                                    <li>
                                        <OwTranslate
                                            id={"tooltip_route_orders_select_all"}
                                            defaultMessage={"Use Select All / Unselect All in a section header to select a whole section."}
                                        />
                                    </li>
                                </ul>
                            }
                        />
                    </div>
                    <div className={"d-flex flex-column mb-4"}>
                        {isLoadingRoutes ? (
                            <div className="text-center p-3">
                                <i className="fas fa-spinner fa-spin" />
                            </div>
                        ) : (
                            routeGroups.map((group, index) => (
                                <DroppableRouteSection
                                    key={group.route?.id ?? "unassigned"}
                                    group={group}
                                    groupIndex={index}
                                    onDrop={moveOrders}
                                    onRemove={removeRouteGroup}
                                    selectedOrderIds={selectedOrderIds}
                                    draggingIds={draggingIds}
                                    onToggleSelect={toggleOrderSelection}
                                    onDragStart={onDragStart}
                                    onDragEnd={onDragEnd}
                                    onSetGroupSelection={setGroupSelection}
                                    legend={deliveryDateLegend}
                                    routeDate={formik.values.route_date}
                                />
                            ))
                        )}
                    </div>

                    <div>
                        <OwButton type={"submit"} title={"save"} disableOnActive={true} disabled={isDisabled} active={isActive} />
                    </div>
                </form>
            </div>

            {/* Do Not Ship Before Warning Modal */}
            <Modal
                isOpen={showDoNotShipWarningModal}
                contentLabel="Do Not Ship Before Warning"
                shouldCloseOnOverlayClick={false}
                className="modalContentClass d-flex justify-content-center"
                overlayClassName="modalOverlayClass"
            >
                <DailyRouteDoNotShipWarningModal
                    excludedOrders={doNotShipExcludedOrders}
                    routeDate={pendingFormValues?.route_date || ""}
                    proceedFn={confirmDoNotShipRemoval}
                    cancelFn={cancelDoNotShipWarning}
                />
            </Modal>

            {/* Status Warning Modal */}
            <Modal
                isOpen={showStatusWarningModal}
                contentLabel="Order Status Warning"
                shouldCloseOnOverlayClick={false}
                className="modalContentClass d-flex justify-content-center"
                overlayClassName="modalOverlayClass"
            >
                <DailyRouteStatusWarningModal
                    ordersNeedingStatusChange={ordersNeedingStatusChange}
                    proceedFn={confirmProceed}
                    cancelFn={cancelStatusWarning}
                />
            </Modal>
        </div>
    );
};
